import { Link, Outlet } from '@tanstack/react-router';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { LocaleSwitcher } from '@/lib/i18n/LocaleSwitcher';
import { deviceLocale } from '@/lib/i18n/locales';
import { useAppLocale } from '@/lib/i18n/useAppLocale';
import { OfflineBanner } from '@/lib/offline/OfflineBanner';

import { NativeShell } from './NativeShell';

/**
 * BP-01 §6: "mobile 390 px `ClientShell`".
 *
 * A single column capped at 390 px and centred, so the same markup is the phone
 * layout §11 tests and a sane narrow card on a desktop window.
 *
 * BP-11 §6 wraps it in `NativeShell`, which supplies the status-bar colour,
 * the safe-area insets, the Android back button and the splash hide. All four
 * are no-ops in a browser, so the PWA and the installed app render the same
 * tree — which is what keeps the native path from drifting.
 */
export function ClientShell(): JSX.Element {
  const { t } = useTranslation('common');
  // BP-19 rule 6 and §14's applied default: the device's language when it is one of
  // the four, else `en`. No tenant default — the journey is anonymous until the OTP
  // step and has no session to read one from.
  useAppLocale(deviceLocale(window.navigator));

  return (
    <NativeShell>
      <div className="min-h-screen bg-muted/60 text-foreground" data-testid="client-shell">
        <OfflineBanner />
        {/* BP-36 rule 5 (Amit, 2026-11-02): no fixed 390 px column. The page takes the
            width it is given, up to a desktop's reading limit, and each screen sets its own
            columns by the width of `main` (a size container), which is also how it follows
            the device's orientation: 768 px across in portrait, 1024 px in landscape. */}
        <div className="mx-auto w-full max-w-7xl p-4 min-[768px]:px-6">
          <header className="mb-4 flex items-baseline justify-between">
            <Link to="/" className="text-lg font-semibold">
              {t('app.client')}
            </Link>
            <div className="flex items-center gap-3">
              {/* BP-19 §6. No `persist`: this header renders for an anonymous
                  visitor as often as not, and `PUT /me/locale` needs a session.
                  The choice is kept in `localStorage` and survives the reload. */}
              <LocaleSwitcher />
              <Link to="/" className="text-sm underline">
                {t('nav.signIn')}
              </Link>
            </div>
          </header>
          <main className="@container">
            <Outlet />
          </main>
        </div>
      </div>
    </NativeShell>
  );
}
